import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import "./GroupCardShared.scss";
import "./GroupConfigCard.scss";

import { SectionRole } from "../../../../shared/types/roles";
import { toastLocalizedOperationCatcher } from "../../../../utils/alerts";
import { reorderItemInCollection } from "../../../../utils/collections";

import { serverDistributeUsersToGroups } from "api/api-server-actions";
import { GroupConfigBreakoutsBanner } from "components/materials/page/groups/GroupConfigBreakoutsBanner";
import { PageGroupEditCard } from "components/materials/page/groups/PageGroupEditCard";
import { StudentOptionsMenu } from "components/materials/page/groups/StudentOptionsMenu";
import {
  DraggablePageGroupUserType,
  DraggableSectionUserType,
  getUngroupedStudentUsers,
  hasTotalUsersReachedMaxGroupSize,
} from "components/materials/page/groups/helpers";
import { GroupSizeConfig } from "components/materials/page/groups/menus/GroupSizeConfig";
import { InteractionModeConfig } from "components/materials/page/groups/menus/InteractionModeConfig";
import { ViewGroupMenu } from "components/materials/page/groups/menus/ViewGroupMenu";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { userActivityIcons } from "components/materials/section/helpers";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Card } from "mds/components/Card";
import { CloseButton } from "mds/components/CloseButton";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, LoadingDotsIcon, PersonSingleIcon } from "mds/icons";
import { useUserIdsWhoCanParticipateInSection } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  NestedPageGroupType,
  SectionUserActivityStatus,
  selectCurrentSectionIsArchived,
  selectCurrentSectionIsTemplate,
  selectHasAssessmentsForPageId,
  selectHasSubmissionsForPageId,
  selectUsersWithSectionActivityInCurrentSection,
} from "store/selectors";
import { selectAssessedSubmissionsByPageId } from "store/selectors/assessment";
import { sortByActiveStateThenFirstName } from "store/selectors/sorting";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { useAv } from "utils/av";
import { getUserFullName } from "utils/user-utils";

interface GroupConfigCardProps {
  page: PageType;
  pageGroups: NestedPageGroupType[];
}

export const GroupConfigCard: FC<GroupConfigCardProps> = ({ page, pageGroups }) => {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);

  const hideGroupConfig = useCallback(
    () => dispatch(viewStateActions.setShowGroupConfig(false)),
    [dispatch],
  );

  const submissionsExist = useAppSelector((s) => selectHasSubmissionsForPageId(s, page.id));
  const hasAssessments = useAppSelector((s) => selectHasAssessmentsForPageId(s, page.id));
  const assessedSubmissions = useAppSelector((s) => selectAssessedSubmissionsByPageId(s, page.id));
  const groupIdsWithAssessments = new Set(
    assessedSubmissions.filter((s) => s.page_group_id).map((s) => s.page_group_id),
  );
  const isAssessing = useIsAssessing();
  const studentUserIds = useUserIdsWhoCanParticipateInSection();
  const users = useListSelector(selectUsersWithSectionActivityInCurrentSection);
  const studentUsers = useMemo(
    () =>
      users
        .filter((user) => studentUserIds.includes(user.id))
        .toSorted(sortByActiveStateThenFirstName),
    [users, studentUserIds],
  );

  const [isDistributingStudents, setIsDistributingStudents] = useState(false);
  const hasSubmissions = Boolean(submissionsExist || page.submissions_last_collected_at);
  const isReadOnly = hasAssessments || isAssessing || isArchived;
  const showDeletePageGroupButton = !isAssessing && !isArchived;

  const [localUngrouped, setLocalUngrouped] = useState<DraggableSectionUserType[] | null>(null);
  const derivedUngrouped = getUngroupedStudentUsers(pageGroups, studentUsers);
  const ungroupedStudentUsers = localUngrouped ?? derivedUngrouped;

  // Reset local override when group membership (for these pageGroups) changes
  const membershipHash = useMemo(() => {
    return pageGroups
      .flatMap((pg) => pg.page_group_users.map((pgu) => `${pg.id}:${pgu.section_user_id}`))
      .sort()
      .join("|");
  }, [pageGroups]);
  useEffect(() => {
    if (localUngrouped !== null) {
      setLocalUngrouped(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipHash]);

  // moveSectionUserLocally handles drag events in both GroupConfigCard and PageGroupEditCard. All it
  // does is update the page_group_id of the dragged item and set it equal to the dropContainerId
  // of whatever drop zone it's hovering over. This way when onDropSectionUser is called, the item
  // will have the new value for page_group_id already set. The value of dropContainerId will be
  // a PageGroup id when hovering over a PageGroupEditCard, or the special value "ungrouped" when
  // hovering over the ungrouped students list.
  const moveSectionUserLocally = (
    item: DraggableSectionUserType | DraggablePageGroupUserType,
    _toIndex: number,
    dropContainerId: string,
  ) => {
    // eslint-disable-next-line no-param-reassign
    item.page_group_id = dropContainerId;
  };

  // moveRemovedStudentToTop is passed down through PageGroupEditCard into StudentOptionsMenu where it is
  // used to handle clicks on the "Remove from this Group" button. StudentOptionsMenu performs the
  // DELETE request on the PageGroupUser which causes it to disappear from the PageGroupEditCard and appear
  // in the ungrouped students list, and then moveRemovedStudentToTop moves it to the top of that list.
  const moveRemovedStudentToTop = (studentSectionUserId: string) => {
    const removed = studentUsers.find((u) => u.section_user_id === studentSectionUserId);
    if (!removed) return;
    const item: DraggableSectionUserType = { ...removed, order: -1 };
    const collection: DraggableSectionUserType[] = ungroupedStudentUsers.map((u) => ({ ...u }));
    const newCollection = reorderItemInCollection(item, collection, 0);
    setLocalUngrouped(newCollection);
  };

  // onDropSectionUser handles drop events in both GroupConfigCard and PageGroupEditCard. It consults
  // the value of page_group_id set on the dragged item in moveSectionUserLocally and the value of
  // sourceContainerId from the drop event and performs the appropriate API request. The value of
  // sourceContainerId will be the special value "ungrouped" when the item is being dragged out
  // of the ungrouped students list, or a PageGroup id when being dragged out of a PageGroupEditCard.
  const onDropSectionUser = (
    droppedItem: DraggableSectionUserType | DraggablePageGroupUserType,
    _toIndex: number,
    sourceContainerId: string,
  ) => {
    if (droppedItem.page_group_id === sourceContainerId) {
      // No-op if the item is being dropped back into the same container it came from.
      return;
    }

    if (sourceContainerId === "ungrouped") {
      // We are grabbing from the ungrouped students list, but they might be just unready in
      // which case they already have a PageGroupUser that needs to be updated rather than created.
      const pageGroupUserId = pageGroups
        .flatMap((pg) => pg.page_group_users)
        .find((pgu) => pgu.section_user_id === droppedItem.section_user_id)?.id;

      if (pageGroupUserId) {
        // Existing PageGroupUser found, so update it to be ready and in the new PageGroup.
        storeApi.page_group_users.partial_update({
          id: pageGroupUserId,
          page_group_id: droppedItem.page_group_id,
          is_ready: true,
        });
      } else {
        // No existing PageGroupUser, so create a new one.
        storeApi.page_group_users.create({
          section_id: page.section_id,
          page_group_id: droppedItem.page_group_id,
          section_user_id: droppedItem.section_user_id,
          is_ready: true,
        });
      }
    } else if (droppedItem.page_group_id === "ungrouped") {
      // A DraggablePageGroupUserType object is being dragged from a PageGroupEditCard and dropped
      // into the ungrouped students list, and the corresponding PageGroupUser needs to be deleted.
      storeApi.page_group_users.destroy(droppedItem.id);
    } else {
      // A DraggablePageGroupUserType object is being dragged from one PageGroupEditCard and dropped
      // into another, and the corresponding PageGroupUser needs to be updated.
      storeApi.page_group_users.partial_update({
        id: droppedItem.id,
        page_group_id: droppedItem.page_group_id,
        is_ready: true,
      });
    }
  };

  // If all section_users are grouped but there is a group that exceeds the max group size limit,
  // the Distribute Students button should be re-enabled.
  const isAssignedGroupsInteractionMode = page.interaction_mode === InteractionMode.ASSIGN_STUDENTS;
  const isStudentSelfAssignInteractionMode = page.interaction_mode === InteractionMode.SELF_ASSIGN;
  const areAllStudentsGrouped = ungroupedStudentUsers.length === 0;
  const isMaxGroupSizeSet = page.max_group_size !== null;
  // Consider a group at or above the size limit as a candidate for redistribution.
  const isAnyGroupAtOrOverSizeLimit = pageGroups.some((pg) =>
    hasTotalUsersReachedMaxGroupSize(pg, page),
  );
  const noStudentsLeftToDistribute =
    areAllStudentsGrouped && (!isMaxGroupSizeSet || !isAnyGroupAtOrOverSizeLimit);
  const isDistributeDisabled =
    !isAssignedGroupsInteractionMode ||
    noStudentsLeftToDistribute ||
    isDistributingStudents ||
    isReadOnly;
  const distributeStudentsButtonTooltip = hasAssessments
    ? t("tooltip.group_config_has_assessments")
    : noStudentsLeftToDistribute
      ? t("tooltip.distribute.no_students")
      : t("tooltip.distribute.default");

  const onDistributeStudents = () => {
    setIsDistributingStudents(true);
    const sectionUserIds = isInVirtualClass
      ? users
          .filter(
            (u) =>
              u.role === SectionRole.STUDENT &&
              u.sectionActivityStatus !== SectionUserActivityStatus.OFFLINE,
          )
          .map((u) => u.section_user_id)
      : null;

    serverDistributeUsersToGroups(page.id, sectionUserIds)
      .catch(() => toastLocalizedOperationCatcher("failed_to_distribute_users_to_groups"))
      .finally(() => setIsDistributingStudents(false));
  };

  const isResetGroupsDisabled =
    pageGroups.reduce((acc, curr) => curr.page_group_users.length + acc, 0) === 0 || isReadOnly;

  const onResetGroups = () => {
    // TODO: This should be a batch delete all or delete many.
    pageGroups.forEach((pageGroup) => {
      pageGroup.page_group_users.forEach((pageGroupUser) => {
        storeApi.page_group_users.destroy(pageGroupUser.id);
      });
    });
  };

  if (isInTemplateSection) {
    return (
      <div className="group-config-card group-card flex w-full flex-col items-center justify-start gap-4 rounded-2xl border border-black-tint-90 bg-black-tint-97 px-5 py-4">
        <div className="flex w-full flex-shrink-0 flex-wrap justify-between">
          <div className="h2 truncate">{t("group_configuration_card.groups")}</div>
          <CloseButton
            aria-label={t("group_configuration_card.tooltip.close_group_settings")}
            onClick={hideGroupConfig}
          />
        </div>

        <div className="flex w-full flex-shrink-0 flex-wrap gap-2">
          <div className="flex flex-1">
            <InteractionModeConfig
              page={page}
              pageGroups={pageGroups}
              title={t("group_configuration_card.tooltip.group_selection_settings")}
            />
          </div>

          <div className="flex flex-1">
            <GroupSizeConfig page={page} title={t("tooltip.group_size")} />
          </div>
        </div>

        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-xs text-black-tint-40">
            {t("group_configuration_card.template_hint")}
          </span>
          <SquareButton
            eventName="Group settings - Done"
            eventProps={{ pageGroupCount: pageGroups.length }}
            kind="accent"
            size="s"
            onClick={hideGroupConfig}
          >
            {t("common.done")}
          </SquareButton>
        </div>
      </div>
    );
  }

  // Max height calculation subtracts UI elements from viewport height:
  // - --navbar-height, --footer-height: main navigation elements
  // - --participant-bar-height: virtual class participant bar (0px for in-person)
  // - 40px: margins/padding around component
  // - 148px: height of the page title + tags
  return (
    <div className="group-config-card group-card flex min-h-[330px] w-full flex-col items-center justify-start gap-4 overflow-y-auto rounded-2xl border border-black-tint-90 bg-black-tint-97 px-5 py-4 [max-height:calc(100vh-var(--navbar-height)-var(--footer-height)-var(--participant-bar-height)-40px-148px)]">
      <div className="flex w-full flex-shrink-0 flex-wrap justify-between">
        <div className="h2 truncate">{t("group_configuration_card.groups")}</div>
        <div className="flex flex-wrap gap-1">
          {isInVirtualClass && pageGroups.length > 0 && <ViewGroupMenu pageGroups={pageGroups} />}
          <InteractionModeConfig
            isReadOnly={isReadOnly || hasSubmissions}
            page={page}
            pageGroups={pageGroups}
            title={
              hasSubmissions
                ? t("tooltip.group_config_has_submissions")
                : t("group_configuration_card.tooltip.group_selection_settings")
            }
          />

          <CloseButton
            aria-label={t("group_configuration_card.tooltip.close_group_settings")}
            onClick={hideGroupConfig}
          />
        </div>
      </div>

      <GroupConfigBreakoutsBanner hasAssessments={hasAssessments} page={page} />

      {isAssessing && !page.assessments_published_at && (
        <div className="w-full">
          <Banner kind="info">{t("group_configuration_card.anonymity_alert")}</Banner>
        </div>
      )}

      {/* Using min-h-[8rem] to ensure a minimum height for the panel */}
      <div className="flex min-h-[8rem] w-full min-w-0 max-w-full flex-1 gap-3 overflow-x-hidden overflow-y-hidden">
        <div className="panel flex min-h-[8rem] min-w-0 flex-1 flex-col gap-3">
          <DragAndDrop
            className="flex min-h-[8rem] w-full flex-1"
            dragItemKind="section_user"
            dropContainerId="ungrouped"
            droppable={!isReadOnly}
            onDraggedItemHover={moveSectionUserLocally}
          >
            <Card
              className="flex min-h-[8rem] flex-1 flex-col overflow-hidden"
              data-testid="students-card"
            >
              <div className="mb-1 flex w-full justify-between">
                <div className="h3 ml-2 truncate">{t("group_configuration_card.students")}</div>
                <div className="h6 flex items-center justify-center gap-1 px-2 text-black-tint-40">
                  <PersonSingleIcon />
                  {ungroupedStudentUsers.length}/{studentUsers.length}
                </div>
              </div>

              {/* min-h: two default ListItem rows — h-8 (2rem) + my-[2px] (4px vertical) each → 2×(2rem+4px); */}
              <div className="min-h-[4.5rem] flex-1 overflow-y-auto">
                {ungroupedStudentUsers.map((section_user) => (
                  <DragAndDrop
                    className="w-full"
                    draggable={!isReadOnly}
                    dragItemKind="section_user"
                    dropContainerId="ungrouped"
                    item={section_user}
                    key={section_user.id}
                    onDropItem={onDropSectionUser}
                  >
                    <ListItem
                      className="flex w-full items-center justify-between"
                      data-section-user-id={section_user.section_user_id}
                      data-testid={`row-${section_user.section_user_id}`}
                      key={section_user.id}
                      grabbable
                    >
                      <div className="flex min-w-0 items-center gap-1.5">
                        {userActivityIcons[section_user.sectionActivityStatus]}
                        <div className="truncate">{getUserFullName(section_user)}</div>
                      </div>

                      <StudentOptionsMenu
                        currentGroupId={undefined}
                        isInGroup={false}
                        isReadOnly={isReadOnly}
                        page={page}
                        pageGroups={pageGroups}
                        studentSectionUserId={section_user.section_user_id}
                      />
                    </ListItem>
                  </DragAndDrop>
                ))}
              </div>
            </Card>
          </DragAndDrop>
        </div>

        {isDistributingStudents ? (
          <div className="panel flex w-full justify-center">
            <div className="pt-7">
              <LoadingDotsIcon />
            </div>
          </div>
        ) : (
          <div className="panel flex min-h-[8rem] min-w-0 flex-[0_0_50%] flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex flex-col gap-3">
                {pageGroups.map((pageGroup, index) => (
                  <PageGroupEditCard
                    hasAssessedSubmissions={groupIdsWithAssessments.has(pageGroup.id)}
                    isReadOnly={isReadOnly}
                    key={pageGroup.id}
                    moveSectionUserLocally={moveSectionUserLocally}
                    number={index + 1}
                    page={page}
                    pageGroup={pageGroup}
                    pageGroups={pageGroups}
                    showDeleteButton={showDeletePageGroupButton}
                    totalPageGroups={pageGroups.length}
                    onDropSectionUser={onDropSectionUser}
                    onRemove={moveRemovedStudentToTop}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex w-full flex-shrink-0 flex-wrap justify-between">
        <Tooltip
          content={
            hasAssessments ? t("tooltip.group_config_has_assessments") : t("tooltip.reset_groups")
          }
        >
          <SquareButton
            disabled={isResetGroupsDisabled}
            eventName="Group settings - Reset Groups"
            kind="ghost"
            size="s"
            onClick={onResetGroups}
          >
            {t("group_configuration_card.reset")}
          </SquareButton>
        </Tooltip>

        <div className="flex flex-wrap gap-1">
          <SquareButton
            className="add-page-group"
            disabled={isReadOnly}
            eventName="Group settings - Add new group"
            kind="tertiary"
            size="s"
            onClick={() =>
              storeApi.page_groups.create({ page_id: page.id, section_id: page.section_id })
            }
          >
            <IconText iconStart={<AddPlusIcon />} text={t("common.add.page_group")} />
          </SquareButton>

          {isAssignedGroupsInteractionMode && (
            <>
              <GroupSizeConfig
                isReadOnly={isReadOnly}
                page={page}
                title={
                  hasAssessments
                    ? t("tooltip.group_config_has_assessments")
                    : t("tooltip.group_size")
                }
              />

              <Tooltip content={isDistributeDisabled ? distributeStudentsButtonTooltip : ""}>
                <SquareButton
                  disabled={isDistributeDisabled}
                  eventName="Group settings - Distribute students"
                  kind="tertiary"
                  size="s"
                  title={!isDistributeDisabled ? distributeStudentsButtonTooltip : ""}
                  onClick={onDistributeStudents}
                >
                  {isInVirtualClass
                    ? t("group_configuration_card.distribute_online_students")
                    : t("group_configuration_card.distribute_students")}
                </SquareButton>
              </Tooltip>
            </>
          )}

          {isStudentSelfAssignInteractionMode && (
            <GroupSizeConfig
              isReadOnly={isReadOnly}
              page={page}
              title={
                hasAssessments ? t("tooltip.group_config_has_assessments") : t("tooltip.group_size")
              }
            />
          )}

          <SquareButton
            eventName="Group settings - Done"
            eventProps={{ pageGroupCount: pageGroups.length }}
            kind="accent"
            size="s"
            onClick={hideGroupConfig}
          >
            {t("common.done")}
          </SquareButton>
        </div>
      </div>
    </div>
  );
};
